import { useContext } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faToggleOff, faToggleOn, faTrashAlt } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import { confirmDelete } from '@functions/entity'

import useAddress, { useAddressForm } from '@hooks/useAddress'
import useSelector from '@hooks/useSelector'

import EntitySelectBox from '@components/EntitySelectBox'
import SuburbSearch from '@components/SuburbSearch'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import type { UseFormPayload } from '@domain-group/react-hooks'

import type { EntitiesState } from '@redux/entities'

import type { GeoSuburbModel } from '@models/types'

const MODAL_KEY = 'CreateOrEditAddressModal'

type HandleSelectItemParams = {
  geoStates: EntitiesState['geoStates'],
  geoSuburb: GeoSuburbModel,
  setEntityState: UseFormPayload['setEntityState'],
}

const handleSelectItem = (params: HandleSelectItemParams) => {
  const { geoStates, geoSuburb, setEntityState } = params

  setEntityState({
    address_postcode: geoSuburb?.post_code,
    address_state: geoStates[geoSuburb.geo_state_id]?.code,
    address_region: geoSuburb?.title,
  })
}

type ModalCallbacks = {
  closeModal: () => void,
  createAddress: (payload: HandleCallbackActionParams) => void,
  setDefaultAddress:(payload: HandleCallbackActionParams) => void,
  updateAddress: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateOrEditAddressModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const {
    closeModal, createAddress, setDefaultAddress, updateAddress,
  } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { address } = modalPayload

  const useAddressPayload = useAddress(address)

  const {
    callbacks: {
      createAddress: createFn,
      deleteAddress: deleteFn,
      updateAddress: updateFn,
      updateDefaultAddress,
    },
    creating,
    updating,
  } = useAddressPayload

  const useAddressFormPayload = useAddressForm(address, { validateOn: 'change' })

  const {
    entityState,
    entityState: {
      address_line_1,
      address_line_2,
      address_postcode,
      address_region,
      address_state,
      geo_country_id,
      is_default,
      title,
    },
    errors,
    handlers,
    saveEnabled,
    setEntityState,
  } = useAddressFormPayload

  const newRecord = !address.id

  const createOrUpdateAddressPayload = {
    callbacks: {
      action: newRecord ? createFn : updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: `Address ${newRecord ? 'Created' : 'Updated'}`,
  }

  const confirmDeleteParams = {
    callbacks: {
      afterAction: closeModal,
      deleteFn,
    },
    strings: {
      deleteString: 'delete',
      entityName: 'Address',
    },
  }

  const setDefaultAddressPayload = {
    callbacks: {
      action: updateDefaultAddress,
      afterAction: closeModal,
    },
    entityParams: {
      ...address,
      is_default: !is_default,
    },
    toastText: 'Default Shipping Address Updated',
  }

  const { geoStates } = useSelector(reduxState => reduxState.entities)

  const searchCallbacks = {
    onChange: (value: string) => setEntityState({ address_region: value }),
    selectItem: (geoSuburb: GeoSuburbModel) => handleSelectItem({ geoStates, geoSuburb, setEntityState }),
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header
        callbacks={callbacks}
        title={newRecord ? 'Create' : 'Edit'}
        titleSecondLine="Shipping Address"
      />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="* Title" errorMessage={errors.title}>
            <input name="title" type="text" value={title} {...handlers} />
          </Form.Field>

          <Form.Field label="* Address Line 1" marginTop="large" errorMessage={errors.address_line_1}>
            <input name="address_line_1" type="text" value={address_line_1} {...handlers} />
          </Form.Field>

          <Form.Field
            errorMessage={errors.address_line_2}
            label="Address Line 2"
            marginTop="large"
          >
            <input name="address_line_2" type="text" value={address_line_2} {...handlers} />
          </Form.Field>

          <Form.Field
            errorMessage={errors.address_region}
            label="* Suburb"
            marginTop="large"
          >
            <SuburbSearch
              callbacks={searchCallbacks}
              requestOptions={{ geo_country_id }}
              value={address_region}
            />
          </Form.Field>

          <Form.Row marginTop={['small', 'large']}>
            <Box flexDirection={['column', 'row']}>
              <Form.Field
                boxProps={{ width: '100%', marginRight: 'medium' }}
                errorMessage={errors.address_postcode}
                label="* Post Code"
                // @ts-expect-error TS2322
                marginTop={['large', 0]}
              >
                <input name="address_postcode" type="text" value={address_postcode} {...handlers} />
              </Form.Field>

              <Form.Field
                boxProps={{ width: ['100%', '100%'] }}
                errorMessage={errors.address_state}
                label="* State"
                // @ts-expect-error TS2322
                marginTop={['large', 0]}
              >
                <input name="address_state" type="text" value={address_state} {...handlers} />
              </Form.Field>
            </Box>
          </Form.Row>

          <Form.Field errorMessage={errors.geo_country_id} label="Country" marginTop="large">
            <EntitySelectBox
              data-value-type="number"
              entityKey="geoCountries"
              // @ts-expect-error TS2322
              name="geo_country_id"
              sortDirection="asc"
              sortKey="title"
              value={geo_country_id}
              {...handlers}
            />
          </Form.Field>

          {!!address.id && (
            <Form.Field label="More Options" marginTop="large">
              {!is_default && (
                <Button
                  buttonStyle="secondaryUtility"
                  icon={<FontAwesomeIcon icon={is_default ? faToggleOn : faToggleOff} />}
                  onClick={() => setDefaultAddress(setDefaultAddressPayload)}
                  size="medium"
                  style={{ marginBottom: 8, width: '100%' }}
                >
                  Set Default
                </Button>
              )}

              <Button
                buttonStyle="secondaryUtility"
                icon={<FontAwesomeIcon icon={faTrashAlt} />}
                // @ts-expect-error TS2345
                onClick={() => confirmDelete(confirmDeleteParams)}
                size="medium"
              >
                Delete Address
              </Button>
            </Form.Field>
          )}
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          loadingBubbleColor="white"
          onClick={
            newRecord
              ? () => createAddress(createOrUpdateAddressPayload)
              : () => updateAddress(createOrUpdateAddressPayload)
          }
          size="large"
        >
          {saveEnabled ? `${newRecord ? 'Create' : 'Update'} Address` : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditAddressModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
